import { useMemo } from 'react';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import type { TableRowData, UseDataTableInstanceOptions } from '../types';
import { useDataTableColumns } from './useDataTableColumns';
import { createSelectionColumn } from '../components/SelectionColumn';

/**
 * Hook for creating a table instance with server-side data fetching.
 *
 * This hook orchestrates two key responsibilities:
 * 1. Data fetching via useTableData (the query hook)
 * 2. Table instance creation via useTableInstance
 *
 * Must be used within TableQueryStateProvider.
 *
 * @example
 * ```tsx
 * const { table, isLoading, isError, error } = useDataTableInstance({
 *   columns,
 *   queryKey: ['users'],
 *   queryFn: fetchUsers,
 * });
 * ```
 */
export function useDataTableInstance<TRow extends TableRowData>({
  columns: columnConfigs,
  data,
  paginationMeta,
  pagination,
  enableRowSelection = false,
  selectAllAcrossPages = false,
  getRowId,
  rowSelection,
  onRowSelectionChange,
  onSelectAllModeToggle,
  getRowAriaLabel,
  enableColumnResizing = false,
  columnSizing,
  onColumnSizingChange,
}: UseDataTableInstanceOptions<TRow>) {
  const columns = useDataTableColumns<TRow>(
    columnConfigs,
    enableColumnResizing
  );

  // Prepend the synthetic checkbox column only when selection is opt-in, so
  // every existing (non-selecting) table is byte-identical. In select-all mode
  // the column inverts its display (rowSelection holds exclusions) and its
  // header becomes a global "clear all" toggle via `onSelectAllModeToggle`.
  const tableColumns = useMemo(
    () =>
      enableRowSelection
        ? [
            createSelectionColumn<TRow>(
              selectAllAcrossPages,
              onSelectAllModeToggle,
              getRowAriaLabel
            ),
            ...columns,
          ]
        : columns,
    [
      enableRowSelection,
      selectAllAcrossPages,
      onSelectAllModeToggle,
      getRowAriaLabel,
      columns,
    ]
  );

  const totalPages =
    Math.ceil(paginationMeta.total / paginationMeta.limit) || 1;

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      pagination,
      ...(enableRowSelection ? { rowSelection: rowSelection ?? {} } : {}),
      ...(columnSizing ? { columnSizing } : {}),
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    enableRowSelection,
    // Only marks columns resizable: the header's resize strip tracks the
    // drag and sets the width once, on release.
    enableColumnResizing,
    ...(onColumnSizingChange ? { onColumnSizingChange } : {}),
    enableMultiRowSelection: enableRowSelection,
    ...(enableRowSelection && onRowSelectionChange
      ? { onRowSelectionChange }
      : {}),
    // Key selection by the domain id (e.g. alertKey) instead of row index so a
    // checked row stays checked across background refetches; the parent also
    // clears selection on filter/search/page change.
    ...(getRowId ? { getRowId } : {}),
    pageCount: totalPages,
    rowCount: paginationMeta.total,
  });

  return table;
}
